'use client'

import { useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { useAuth } from '@/features/auth/hooks'
import { useBatchedStreamCallback } from '@/hooks/use-batched-stream-callback'
import { safeParseFloat } from '@/lib/utils'
import { useActiveAccountsScope } from '@/service/account.hook'
import { useResyncDumpTracker } from '@/service/runtime-health/use-resync-dump-tracker'
import {
  applyPortfolioFrames,
  mergeAccountKeyedRows,
  type PortfolioFrame,
} from '@/service/ws/account-data-manager.helpers'
import {
  type AccountScopedResponse,
  useFeesStream,
  usePortfolioStream,
  useSummaryStream,
} from '@/service/ws/use-orders-stream'
import {
  accountDataLoadingAtom,
  accountFeesByIdAtom,
  accountSummaryByIdAtom,
  balancesLoadedAccountIdsAtom,
  balancesRawAtom,
  totalBalanceUsdRawAtom,
  triggerResyncAtom,
} from '@/state/account'
import type {
  AccountFeesList,
  AccountSummaryList,
  PortfolioDataList,
} from '@/types/generated/common/account'
import { AccountDataStreamType } from '@/types/generated/common/types'

function omitAccountId<T>(prev: Record<string, T>, accountId: string): Record<string, T> {
  if (!(accountId in prev)) return prev
  const { [accountId]: _removed, ...rest } = prev
  return rest
}

function omitAccountIds<T>(
  prev: Record<string, T>,
  accountIds: ReadonlySet<string>
): Record<string, T> {
  let next = prev
  for (const id of accountIds) next = omitAccountId(next, id)
  return next
}

const PORTFOLIO_FLUSH_INTERVAL_MS = 100

type SnapshotFrame<T> = { accountId: string; rows: readonly T[] | undefined }

// Applies a window of latest-value frames in arrival order, so the last push per account wins
// exactly as it did when each frame was its own atom write.
function applySnapshotFrames<T extends { accountId: string }>(
  prev: Record<string, T>,
  frames: readonly SnapshotFrame<T>[]
): Record<string, T> {
  let next = prev
  for (const frame of frames) next = mergeAccountKeyedRows(next, frame.rows, frame.accountId)
  return next
}

/**
 * Owns every active account's Balances (PORTFOLIO) + Summary + Fees
 * subscriptions on a single batched channel each (`accountIds: [...]`
 * instead of one channel per account, per stream type). Summary/Fees have no
 * dump/delta distinction of their own (every push is a full latest-value
 * replace, keyed by whichever account it belongs to), so only Balances needs
 * per-account dump tracking.
 */
function AccountSnapshotCoordinator() {
  const { accountIds, scopeKey, isResolved, hasAccounts } = useActiveAccountsScope()
  const setBalances = useSetAtom(balancesRawAtom)
  const setTotalBalance = useSetAtom(totalBalanceUsdRawAtom)
  const setAccountFees = useSetAtom(accountFeesByIdAtom)
  const setAccountSummary = useSetAtom(accountSummaryByIdAtom)
  const setLoadedAccountIds = useSetAtom(balancesLoadedAccountIdsAtom)
  const triggerResync = useAtomValue(triggerResyncAtom)
  const { isAuthenticated } = useAuth()

  const { trackerRef, syncGeneration, noteDumped, resetTracker } = useResyncDumpTracker('balances')
  const prevIsAuthenticatedRef = useRef(isAuthenticated)
  const prevAccountIdsRef = useRef<string[]>([])

  // Summary and Fees push per account as often as Balances does; coalesce them on the same
  // window so account cards and the order-form fee panel re-render once per window, not per
  // frame. No resetKey: the effects below flush before they drop or clear accounts, so a
  // pending frame is applied first and then removed instead of being lost for a live account.
  const applySummaryFrames = useCallback(
    (frames: readonly SnapshotFrame<AccountSummaryList['accountSummary'][number]>[]) =>
      setAccountSummary((prev) => applySnapshotFrames(prev, frames)),
    [setAccountSummary]
  )
  const { enqueue: enqueueSummary, flush: flushSummary } = useBatchedStreamCallback(
    applySummaryFrames,
    { intervalMs: PORTFOLIO_FLUSH_INTERVAL_MS }
  )
  const applyFeesFrames = useCallback(
    (frames: readonly SnapshotFrame<AccountFeesList['accountFees'][number]>[]) =>
      setAccountFees((prev) => applySnapshotFrames(prev, frames)),
    [setAccountFees]
  )
  const { enqueue: enqueueFees, flush: flushFees } = useBatchedStreamCallback(applyFeesFrames, {
    intervalMs: PORTFOLIO_FLUSH_INTERVAL_MS,
  })

  useEffect(() => {
    resetTracker()
  }, [scopeKey, triggerResync, resetTracker])

  useEffect(() => {
    const wasAuthenticated = prevIsAuthenticatedRef.current
    prevIsAuthenticatedRef.current = isAuthenticated
    if (!wasAuthenticated && isAuthenticated) {
      resetTracker()
    }
  }, [isAuthenticated, resetTracker])

  useEffect(() => {
    if (!isResolved) return
    const dropped = prevAccountIdsRef.current.filter((id) => !accountIds.includes(id))
    if (dropped.length > 0) {
      flushSummary()
      flushFees()
      const droppedSet = new Set(dropped)
      setBalances((prev) => prev.filter((b) => !droppedSet.has(b.accountId)))
      setTotalBalance((prev) => omitAccountIds(prev, droppedSet))
      setAccountFees((prev) => omitAccountIds(prev, droppedSet))
      setAccountSummary((prev) => omitAccountIds(prev, droppedSet))
      setLoadedAccountIds((prev) => omitAccountIds(prev, droppedSet))
    }
    prevAccountIdsRef.current = accountIds
  }, [
    accountIds,
    isResolved,
    setBalances,
    setTotalBalance,
    setAccountFees,
    setAccountSummary,
    setLoadedAccountIds,
    flushSummary,
    flushFees,
  ])

  useEffect(() => {
    if (isResolved && !hasAccounts) {
      flushSummary()
      flushFees()
      setBalances([])
      setTotalBalance({})
      setAccountFees({})
      setAccountSummary({})
      setLoadedAccountIds({})
    }
  }, [
    isResolved,
    hasAccounts,
    setBalances,
    setTotalBalance,
    setAccountFees,
    setAccountSummary,
    setLoadedAccountIds,
    flushSummary,
    flushFees,
  ])

  const portfolioPayload = useMemo(
    () => ({
      accountId: '',
      accountIds,
      dataType: AccountDataStreamType.ACCOUNT_DATA_STREAM_TYPE_PORTFOLIO,
    }),
    [accountIds]
  )

  // The hub pushes portfolio rows per account several times a second; one
  // atom write per window caps the balance-driven render rate (positions
  // table Spot section, account cards).
  const applyFrames = useCallback(
    (frames: readonly PortfolioFrame[]) => {
      let totals: Record<string, string | null> = {}
      setBalances((prev) => {
        const applied = applyPortfolioFrames(prev, frames)
        totals = applied.totals
        return applied.rows
      })
      const touched = Object.entries(totals)
      if (touched.length === 0) return
      setTotalBalance((prev) => {
        const next = { ...prev }
        for (const [accountId, total] of touched) {
          next[accountId] = total === null ? null : safeParseFloat(total)
        }
        return next
      })
    },
    [setBalances, setTotalBalance]
  )
  const { enqueue, flush } = useBatchedStreamCallback(applyFrames, {
    intervalMs: PORTFOLIO_FLUSH_INTERVAL_MS,
    resetKey: scopeKey,
  })

  const handlePortfolioMessage = useCallback(
    (response: AccountScopedResponse<PortfolioDataList> | undefined) => {
      if (!response?.data.portfolioData) return
      const { accountId, data } = response

      // Before isInitialDump: a resync's fresh dump can beat the effect that
      // resets the tracker, and would otherwise be merged as a delta.
      syncGeneration()

      const isInitialDump = trackerRef.current.isInitialDump(accountId)
      enqueue([{ accountId, rows: data.portfolioData, isInitialDump }])
      if (!isInitialDump) return

      // A dump lands synchronously so the loaded flag never clears ahead of
      // the rows it announces.
      flush()
      setLoadedAccountIds((prev) => ({ ...prev, [accountId]: true }))
      trackerRef.current.markDumped(accountId)
      noteDumped(accountIds)
    },
    [enqueue, flush, setLoadedAccountIds, syncGeneration, noteDumped, trackerRef, accountIds]
  )

  usePortfolioStream({
    connected: true,
    payload: portfolioPayload,
    onMessage: handlePortfolioMessage,
  })

  const summaryPayload = useMemo(
    () => ({
      accountId: '',
      accountIds,
      dataType: AccountDataStreamType.ACCOUNT_DATA_STREAM_TYPE_SUMMARY,
    }),
    [accountIds]
  )

  const handleSummaryMessage = useCallback(
    (response: AccountScopedResponse<AccountSummaryList> | undefined) => {
      // Latest-value snapshot, replace prior per account. Every row, not just
      // the first — see `mergeAccountKeyedRows`.
      if (!response) return
      enqueueSummary([{ accountId: response.accountId, rows: response.data.accountSummary }])
    },
    [enqueueSummary]
  )

  useSummaryStream({
    connected: true,
    payload: summaryPayload,
    onMessage: handleSummaryMessage,
  })

  const feesPayload = useMemo(
    () => ({
      accountId: '',
      accountIds,
      dataType: AccountDataStreamType.ACCOUNT_DATA_STREAM_TYPE_FEES,
    }),
    [accountIds]
  )

  const handleFeesMessage = useCallback(
    (response: AccountScopedResponse<AccountFeesList> | undefined) => {
      // Latest-value snapshot, replace prior per account. Every row, not just
      // the first — see `mergeAccountKeyedRows`.
      if (!response) return
      enqueueFees([{ accountId: response.accountId, rows: response.data.accountFees }])
    },
    [enqueueFees]
  )

  useFeesStream({
    connected: true,
    payload: feesPayload,
    onMessage: handleFeesMessage,
  })

  return null
}

/**
 * Sets the "balances" loading flag: cleared only once every currently-active
 * account has completed its first balances dump, not just the first one to
 * finish. Mount once, alongside `<AccountDataManager />` and
 * `<ActivePositionsSync />`.
 */
export function ActiveAccountSnapshotSync() {
  const { accountIds } = useActiveAccountsScope()
  const loadedAccountIds = useAtomValue(balancesLoadedAccountIdsAtom)
  const setLoading = useSetAtom(accountDataLoadingAtom)

  useEffect(() => {
    const allLoaded = accountIds.length === 0 || accountIds.every((id) => loadedAccountIds[id])
    setLoading((prev) => (prev.balances === !allLoaded ? prev : { ...prev, balances: !allLoaded }))
  }, [accountIds, loadedAccountIds, setLoading])

  return <AccountSnapshotCoordinator />
}
